import type { TypedDocumentNode } from '@apollo/client';
import { gql, useMutation } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase';
import { IconButton } from '@sorare/core/src/atoms/buttons/IconButton';
import { Dropdown } from '@sorare/core/src/atoms/dropdowns/Dropdown';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { iconEyeOff } from '@sorare/core/src/atoms/icons/system/iconEyeOff.ts';
import { iconMore } from '@sorare/core/src/atoms/icons/system/iconMore.ts';
import { iconPen } from '@sorare/core/src/atoms/icons/system/iconPen.ts';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LabelL } from '@sorare/core/src/atoms/typography';
import type { LineupStyle } from '@sorare/core/src/components/lobby/ContenderLayout/types';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useFeatureFlags } from '@sorare/core/src/hooks/useFeatureFlags';
import { fantasy } from '@sorare/core/src/lib/glossary';

import { usePreferredLineupStyle } from '#src/hooks/usePreferredLineupStyle.ts';
import { getComposeTeamRoute } from '#src/lib/so5.ts';

import type {
  LineupActionsDropdown_so5LeaderboardContender,
  UpdateSo5LineupSettingsMutation,
  UpdateSo5LineupSettingsMutationVariables,
} from './__generated__/index.graphql.ts';
import { InlineLineupIcon } from './assets/InlineLineupIcon.tsx';
import { MoveToBottomIcon } from './assets/MoveToBottomIcon.tsx';
import { MoveToTopIcon } from './assets/MoveToTopIcon.tsx';
import { SpreadLineupIcon } from './assets/SpreadLineupIcon.tsx';

const Root = styled(Vertical).attrs({ gap: 0 })`
  background: var(--c-nd-50-opaque);
  border-radius: var(--unit);

  & > * {
    flex: 1;
    &:not(:last-child) {
      border-bottom: 1px solid var(--c-nd-50);
    }
  }
`;
const StyledButtonBase = styled(ButtonBase)`
  display: block;
  min-width: 180px;
  padding: var(--unit) var(--double-unit);
  transition: background-color 0.2s ease-in-out;
  &:hover {
    background-color: rgba(var(--c-rgb-white), 0.05);
  }

  > ${Horizontal} {
    svg {
      width: var(--double-unit);
    }
  }
`;

const UPDATE_SO5_LINEUP_SETTINGS_MUTATION = gql`
  mutation UpdateSo5LineupSettingsMutation(
    $input: updateSo5LineupSettingsInput!
  ) {
    updateSo5LineupSettings(input: $input) {
      so5Lineup {
        id
        hidden
      }
    }
  }
` as TypedDocumentNode<
  UpdateSo5LineupSettingsMutation,
  UpdateSo5LineupSettingsMutationVariables
>;

type Props = {
  forceMode?: LineupStyle;
  hasEditLineupButton?: boolean;
  hasHideTeamButton?: boolean;
  showHideTeamDialog: () => void;
  so5LeaderboardContender?: LineupActionsDropdown_so5LeaderboardContender | null;
  onLineupUpdated?: () => void;
};
export const LineupActionsDropdown = ({
  forceMode,
  hasEditLineupButton,
  hasHideTeamButton,
  showHideTeamDialog,
  so5LeaderboardContender,
  onLineupUpdated,
}: Props) => {
  const {
    flags: { useEnableHideLineup = false },
  } = useFeatureFlags();
  const [updateSo5LineupSettings] = useMutation(
    UPDATE_SO5_LINEUP_SETTINGS_MUTATION
  );

  const { sport } = useSportContext();
  const [lineupStyle, setLineupStyle] = usePreferredLineupStyle();

  const { so5Leaderboard, managerTeam, so5Lineup } =
    so5LeaderboardContender || {};

  const onUpdateVisibility = async (hidden: boolean) => {
    if (!so5Lineup) return;
    await updateSo5LineupSettings({
      variables: {
        input: { so5LineupId: so5Lineup.id, hidden },
      },
    });
    onLineupUpdated?.();
  };

  const content = (mode: LineupStyle) => (
    <Root>
      {!!(hasEditLineupButton || hasHideTeamButton) && (
        <>
          {!!hasHideTeamButton && (
            <StyledButtonBase onClick={showHideTeamDialog}>
              <Horizontal>
                <FontAwesomeIcon icon={iconEyeOff} />
                <LabelL>
                  <FormattedMessage {...fantasy.hideTeam} />
                </LabelL>
              </Horizontal>
            </StyledButtonBase>
          )}
          {!!hasEditLineupButton && (
            <StyledButtonBase
              to={
                so5Leaderboard
                  ? getComposeTeamRoute({
                      leaderboardSlug: so5Leaderboard?.slug,
                      eventType: so5Leaderboard?.so5Fixture.type.toLowerCase(),
                      managerTeamId: managerTeam?.id,
                      lineupId: so5Lineup?.id,
                      sport,
                    })
                  : ''
              }
            >
              <Horizontal>
                <FontAwesomeIcon icon={iconPen} />
                <LabelL>
                  <FormattedMessage {...fantasy.editLineup} />
                </LabelL>
              </Horizontal>
            </StyledButtonBase>
          )}
        </>
      )}
      {!!so5Lineup && !!useEnableHideLineup && (
        <StyledButtonBase
          onClick={() => {
            onUpdateVisibility(!so5Lineup.hidden);
          }}
        >
          <Horizontal>
            {so5Lineup.hidden ? <MoveToTopIcon /> : <MoveToBottomIcon />}
            <LabelL>
              {so5Lineup.hidden ? (
                <FormattedMessage defaultMessage="Move to top" />
              ) : (
                <FormattedMessage defaultMessage="Move to bottom" />
              )}
            </LabelL>
          </Horizontal>
        </StyledButtonBase>
      )}
      <StyledButtonBase
        onClick={() => setLineupStyle(mode === 'inline' ? 'spread' : 'inline')}
      >
        <Horizontal>
          {mode === 'inline' ? <SpreadLineupIcon /> : <InlineLineupIcon />}
          <LabelL>
            {mode === 'inline' ? (
              <FormattedMessage defaultMessage="Stadium View" />
            ) : (
              <FormattedMessage defaultMessage="Line View" />
            )}
          </LabelL>
        </Horizontal>
      </StyledButtonBase>
    </Root>
  );

  if (forceMode) {
    return content(forceMode);
  }

  return (
    <Dropdown
      forceDesktopBehavior
      align="right"
      label={props => (
        <IconButton
          {...props}
          icon={iconMore}
          size="compact"
          color="transparent"
        />
      )}
    >
      {content(lineupStyle)}
    </Dropdown>
  );
};

LineupActionsDropdown.fragments = {
  so5LeaderboardContender: gql`
    fragment LineupActionsDropdown_so5LeaderboardContender on So5LeaderboardContender {
      slug
      so5Lineup {
        id
        hidden
      }
      so5Leaderboard {
        slug
        so5Fixture {
          slug
          type
        }
      }
      managerTeam {
        id
      }
    }
  ` as TypedDocumentNode<LineupActionsDropdown_so5LeaderboardContender>,
};
